import { useEffect, useMemo, useState } from 'react';
import { X, Clock, Leaf, Flame, Utensils } from 'lucide-react';
import type { Category, MenuItem, Restaurant } from '../../App';
import { api } from '../../lib/api';
import { ImageWithFallback } from '../figma/ImageWithFallback';

interface RestaurantMenuModalProps {
  restaurant: Pick<Restaurant, 'id' | 'name'>;
  onClose: () => void;
}

const parseJsonArray = <T,>(value: unknown): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeDietaryTag = (value: unknown): string => {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  const cleaned = raw.replace(/[_\s]+/g, '-').replace(/-+/g, '-');
  if (cleaned === 'nonveg' || cleaned === 'non-veg' || cleaned === 'nonvegetarian' || cleaned === 'non-vegetarian') return 'non-veg';
  if (cleaned === 'veg' || cleaned === 'vegetarian') return 'veg';
  return cleaned;
};

const formatMoney = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);

export function RestaurantMenuModal({ restaurant, onClose }: RestaurantMenuModalProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadMenu = async () => {
      setLoading(true);
      setError('');

      try {
        const [categoriesRes, itemsRes] = await Promise.allSettled([
          api.menu.getCategories(restaurant.id),
          api.menu.getItems(restaurant.id),
        ]);

        if (!isMounted) return;

        const mappedCategories: Category[] =
          categoriesRes.status === 'fulfilled'
            ? (Array.isArray((categoriesRes.value as any)?.data) ? (categoriesRes.value as any).data : [])
                .map((item: any, index: number) => ({
                  id: String(item?.id || '').trim(),
                  restaurantId: restaurant.id,
                  name: String(item?.name || item?.food_category?.name || '').trim(),
                  image: item?.image || undefined,
                  order: Number(item?.display_order ?? item?.displayOrder ?? index),
                  isActive: Boolean(item?.is_active ?? item?.isActive ?? true),
                }))
                .filter((item: Category) => item.id && item.name && item.isActive)
            : [];

        const mappedItems: MenuItem[] =
          itemsRes.status === 'fulfilled'
            ? (Array.isArray((itemsRes.value as any)?.data) ? (itemsRes.value as any).data : [])
                .map((item: any) => ({
                  id: String(item?.id || '').trim(),
                  restaurantId: restaurant.id,
                  categoryId: String(item?.categoryId || item?.category_id || item?.category?.id || '').trim(),
                  name: String(item?.name || '').trim(),
                  description: String(item?.description || '').trim(),
                  price: Number(item?.discountPrice ?? item?.discount_price ?? item?.price ?? 0),
                  image: item?.image || undefined,
                  tags: (() => {
                    const rawTags = Array.isArray(item?.tags) ? item.tags : [];
                    const normalized = rawTags.map((tag: unknown) => normalizeDietaryTag(tag)).filter(Boolean);
                    const isVeg = item?.isVeg ?? item?.is_veg;
                    if (typeof isVeg === 'boolean' && !normalized.includes('veg') && !normalized.includes('non-veg')) {
                      normalized.push(isVeg ? 'veg' : 'non-veg');
                    }
                    return Array.from(new Set(normalized));
                  })(),
                  spicyLevel: Number(item?.spicy_level ?? item?.spicyLevel ?? 0),
                  isActive: Boolean(item?.is_active ?? item?.isActive ?? true),
                  preparationTime: Number(item?.preparation_time ?? item?.preparationTime ?? 20),
                  inStock: Boolean(item?.in_stock ?? item?.inStock ?? item?.isAvailable ?? true),
                  variants: parseJsonArray<any>(item?.variants).map((variant: any) => ({
                    id: String(variant?.id || ''),
                    name: String(variant?.name || ''),
                    price: Number(variant?.price || 0),
                    isDefault: Boolean(variant?.isDefault || variant?.is_default || false),
                  })),
                  addOns: parseJsonArray<any>(item?.addOns ?? item?.addons).map((addon: any) => ({
                    id: String(addon?.id || ''),
                    name: String(addon?.name || ''),
                    price: Number(addon?.price || 0),
                    category: String(addon?.category || 'addon'),
                    source: 'addon' as const,
                  })),
                  customizationGroups: [],
                }))
                .filter((item: MenuItem) => item.id && item.name && item.isActive)
            : [];

        setCategories(mappedCategories.sort((a, b) => a.order - b.order));
        setMenuItems(mappedItems);

        if (!mappedCategories.length && !mappedItems.length) {
          setError('No menu items available for this restaurant right now.');
        }
      } catch {
        if (!isMounted) return;
        setError('Unable to load the menu right now. Please try again.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadMenu();

    return () => {
      isMounted = false;
    };
  }, [restaurant.id]);

  const groupedCategories = useMemo(() => {
    const categoryMap = new Map<string, MenuItem[]>();

    menuItems.forEach(item => {
      const key = item.categoryId || 'uncategorized';
      const current = categoryMap.get(key) || [];
      current.push(item);
      categoryMap.set(key, current);
    });

    const ordered = categories
      .map(category => ({
        id: category.id,
        name: category.name,
        items: categoryMap.get(category.id) || [],
      }))
      .filter(category => category.items.length > 0);

    const uncategorized = categoryMap.get('uncategorized') || [];
    if (uncategorized.length > 0) {
      ordered.push({
        id: 'uncategorized',
        name: 'Chef Specials',
        items: uncategorized,
      });
    }

    if (!ordered.length && menuItems.length) {
      return [
        {
          id: 'all-items',
          name: 'Menu',
          items: menuItems,
        },
      ];
    }

    return ordered;
  }, [categories, menuItems]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm p-4 sm:p-6">
      <div className="mx-auto flex h-full max-w-5xl items-center justify-center">
        <div className="flex max-h-[90vh] w-full flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-slate-950">
          <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 dark:border-slate-800">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-[#60A5FA]">
                <Utensils className="h-4 w-4" />
                <span>Restaurant Menu</span>
              </div>
              <h2 className="mt-1 text-2xl font-bold text-gray-900 dark:text-slate-100">{restaurant.name}</h2>
            </div>
            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            {loading && (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, index) => (
                  <div
                    key={index}
                    className="h-40 animate-pulse rounded-2xl border border-gray-200 bg-gray-50 dark:border-slate-800 dark:bg-slate-900"
                  />
                ))}
              </div>
            )}

            {!loading && error && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-5 text-sm text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                {error}
              </div>
            )}

            {!loading && !error && (
              <div className="space-y-8">
                {groupedCategories.map(category => (
                  <section key={category.id}>
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-100">{category.name}</h3>
                      <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 dark:bg-slate-800 dark:text-slate-300">
                        {category.items.length} items
                      </span>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {category.items.map(item => {
                        const isVeg = item.tags.includes('veg');
                        const isNonVeg = item.tags.includes('non-veg');
                        const spicyLevel = Number(item.spicyLevel ?? 0);

                        return (
                          <article
                            key={item.id}
                            className="overflow-hidden rounded-2xl border border-gray-200 bg-white transition-shadow hover:shadow-lg dark:border-slate-800 dark:bg-slate-900"
                          >
                            {item.image && (
                              <ImageWithFallback
                                src={item.image}
                                alt={item.name}
                                className="h-44 w-full object-cover"
                              />
                            )}

                            <div className="space-y-3 p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    {(isVeg || isNonVeg) && (
                                      <span
                                        className={`inline-flex h-4 w-4 items-center justify-center border text-[10px] ${
                                          isVeg ? 'border-green-600' : 'border-red-600'
                                        }`}
                                      >
                                        <span className={`h-2 w-2 rounded-full ${isVeg ? 'bg-green-600' : 'bg-red-600'}`} />
                                      </span>
                                    )}
                                    <h4 className="truncate text-lg font-semibold text-gray-900 dark:text-slate-100">{item.name}</h4>
                                  </div>
                                  {item.description && (
                                    <p className="mt-2 line-clamp-2 text-sm text-gray-600 dark:text-slate-300">{item.description}</p>
                                  )}
                                </div>
                                {!item.inStock && (
                                  <span className="rounded-full bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600 dark:bg-red-500/10 dark:text-red-300">
                                    Unavailable
                                  </span>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="rounded-full bg-blue-50 px-2.5 py-1 font-semibold text-[#60A5FA] dark:bg-blue-500/10 dark:text-blue-300">
                                  {formatMoney(item.price)}
                                </span>
                                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-gray-600 dark:bg-slate-800 dark:text-slate-300">
                                  <Clock className="h-3.5 w-3.5" />
                                  {item.preparationTime} min
                                </span>
                                {isVeg && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-green-700 dark:bg-green-500/10 dark:text-green-300">
                                    <Leaf className="h-3.5 w-3.5" />
                                    Veg
                                  </span>
                                )}
                                {spicyLevel > 0 && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-orange-700 dark:bg-orange-500/10 dark:text-orange-300">
                                    <Flame className="h-3.5 w-3.5" />
                                    Spice {spicyLevel}
                                  </span>
                                )}
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
